import { analyzeCategory } from '../../../utils/categoryClassifier';
import { hasStrongBehaviorActionSignal, hasStrongMedicationSignal } from './importLogsParsingClassification';
import {
  CARE_MEDICATION_DOSE_ANY_UNIT_PATTERN,
  CARE_MEDICATION_OBJECT_SIGNAL_PATTERN,
} from '../../../shared/careMedicationPatterns';
import {
  LONG_NOTE_ACTIVITY_DOMINANT_PATTERNS,
  LONG_NOTE_ACTIVITY_PATTERNS,
  LONG_NOTE_BEHAVIOR_DOMINANT_PATTERNS,
  LONG_NOTE_FOOD_CONSUMPTION_PATTERNS,
  LONG_NOTE_FOOD_CONTEXT_PATTERNS,
  LONG_NOTE_MEDICATION_SIDE_EFFECT_PATTERNS,
  LONG_NOTE_MILESTONE_DOMINANT_PATTERNS,
  LONG_NOTE_MOOD_PATTERNS,
  LONG_NOTE_SLEEP_PATTERNS,
} from './importLogsLongNotePatterns';

const normalizeCategory = (value = '') => {
  const normalized = String(value || '').trim().toLowerCase();
  return normalized || null;
};

const resolveCandidateText = (candidateRow = {}) => String(
  candidateRow?.body
  || candidateRow?.note
  || candidateRow?.text
  || candidateRow?.content
  || candidateRow?.rawSourceText
  || ''
).trim();

const hasStrongLongNoteFoodSignal = (text = '') => {
  const comparable = String(text || '').trim();
  if (!comparable) {
    return false;
  }

  const hasConsumption = LONG_NOTE_FOOD_CONSUMPTION_PATTERNS.some((pattern) => pattern.test(comparable));
  const hasFoodContext = LONG_NOTE_FOOD_CONTEXT_PATTERNS.some((pattern) => pattern.test(comparable));
  const hasMealHeader = /\b(?:breakfast|lunch|dinner|snack|meal)\b\s*(?:was|:)/i.test(comparable);

  if (hasMealHeader) {
    return true;
  }

  if (hasConsumption && hasFoodContext) {
    return true;
  }

  if (hasConsumption && /\b(?:before school|before leaving for school|after breakfast|after lunch|after dinner|at school pickup)\b/i.test(comparable)) {
    return true;
  }

  return false;
};

const addCategoryScore = (scores, category, points) => {
  scores[category] = (scores[category] || 0) + points;
};

const scoreLongNoteDominantCategory = (text = '') => {
  const comparable = String(text || '').trim();
  const scores = {
    medication: 0,
    behavior: 0,
    activity: 0,
    food: 0,
    mood: 0,
    sleep: 0,
    milestone: 0,
  };

  if (!comparable) {
    return { category: null, scores };
  }

  if (hasStrongMedicationSignal(comparable)) {
    addCategoryScore(scores, 'medication', 6);
    if (/\b(?:given|gave|administered|took|dose|medication|meds?|medicine)\b/i.test(comparable)) {
      addCategoryScore(scores, 'medication', 2);
    }
    if (CARE_MEDICATION_DOSE_ANY_UNIT_PATTERN.test(comparable)) {
      addCategoryScore(scores, 'medication', 2);
    }
    if (LONG_NOTE_MEDICATION_SIDE_EFFECT_PATTERNS.some((pattern) => pattern.test(comparable))) {
      addCategoryScore(scores, 'medication', 2);
    }
  }

  if (hasStrongBehaviorActionSignal(comparable)) {
    addCategoryScore(scores, 'behavior', 6);
  }

  if (LONG_NOTE_BEHAVIOR_DOMINANT_PATTERNS.some((pattern) => pattern.test(comparable))) {
    addCategoryScore(scores, 'behavior', 3);
  }

  if (LONG_NOTE_ACTIVITY_PATTERNS.some((pattern) => pattern.test(comparable))) {
    addCategoryScore(scores, 'activity', 4);
  }

  if (LONG_NOTE_ACTIVITY_DOMINANT_PATTERNS.some((pattern) => pattern.test(comparable))) {
    addCategoryScore(scores, 'activity', 2);
  }

  if (hasStrongLongNoteFoodSignal(comparable)) {
    addCategoryScore(scores, 'food', 4);
  } else {
    if (/\b(?:breakfast|lunch|dinner|snack|meal)\b/i.test(comparable)) {
      addCategoryScore(scores, 'food', 1);
    }
    if (/\b(?:yogurt|rice|chicken|cucumber|bagel|cream cheese|eggs|strawberries?|juice|crackers|chips|apple|pasta|water bottle|milk|macaroni|nuggets|granola bar)\b/i.test(comparable)) {
      addCategoryScore(scores, 'food', 1);
    }
  }

  if (LONG_NOTE_MOOD_PATTERNS.some((pattern) => pattern.test(comparable))) {
    addCategoryScore(scores, 'mood', 3);
  }

  if (LONG_NOTE_MILESTONE_DOMINANT_PATTERNS.some((pattern) => pattern.test(comparable))) {
    addCategoryScore(scores, 'milestone', 4);
  }

  if (LONG_NOTE_SLEEP_PATTERNS.some((pattern) => pattern.test(comparable))) {
    addCategoryScore(scores, 'sleep', hasStrongMedicationSignal(comparable) ? 1 : 3);
  }

  if (/\b(?:before lunch|at lunch|during lunch|shortly before lunch|after lunch|before dinner|during dinner|after dinner|around bedtime|before bedtime|after medication|during the evening|in the evening|this morning|this afternoon|in line|during music therapy|during group reading time|during recess|during class|during school|at school)\b/i.test(comparable)) {
    addCategoryScore(scores, 'mood', 1);
    addCategoryScore(scores, 'activity', 1);
  }

  if (scores.medication > 0) {
    scores.sleep = Math.min(scores.sleep, 1);
  }

  if (scores.medication > 0 && scores.food > 0 && /\b(?:reduced appetite|no appetite|less appetite|appetite change|side effect|side effects|nausea|stomach)\b/i.test(comparable)) {
    scores.food = Math.max(0, scores.food - 2);
    scores.medication += 2;
  }

  if (scores.activity > 0 && /\b(?:therapy|exercise|tolerat(?:ed|ing)|followed directions|focused|group work|project|improv(?:ed|ement)|progress|independent(?:ly)?)\b/i.test(comparable)) {
    scores.activity += 2;
    scores.mood = Math.max(0, scores.mood - 1);
  }

  if (scores.behavior > 0 && scores.food > 0 && /\b(?:dinner|lunch|breakfast|snack|meal)\b/i.test(comparable) && !/\b(?:incident|escalat(?:ed|ion)|yell(?:ed|ing)?|push(?:ed|ing)?|hit(?:ting)?|kick(?:ed|ing)?|redirect(?:ed|ion)?|calm corner|deep breathing|sensory squeezes?|overstimulated|behavior[- ]wise)\b/i.test(comparable)) {
    scores.behavior = Math.max(0, scores.behavior - 2);
  }

  if (scores.mood > 0 && /\bbedtime\b/i.test(comparable) && !/\b(?:fell asleep|asleep|woke up|woke)\b/i.test(comparable)) {
    scores.sleep = Math.min(scores.sleep, 1);
  }

  const ranking = ['medication', 'behavior', 'activity', 'food', 'mood', 'sleep', 'milestone'];

  const winner = ranking.reduce((best, category) => {
    if (!best) {
      return category;
    }
    return (scores[category] || 0) > (scores[best] || 0) ? category : best;
  }, null);

  const winnerScore = winner ? scores[winner] || 0 : 0;
  if (!winner || winnerScore <= 0) {
    return { category: null, scores };
  }

  const positiveScores = Object.entries(scores).filter(([, score]) => score > 0);
  const topTwo = positiveScores.sort((a, b) => b[1] - a[1]).slice(0, 2);
  if (topTwo.length >= 2 && topTwo[0][1] === topTwo[1][1]) {
    const tieWinner = ranking.find((category) => positiveScores.some(([name]) => name === category && scores[category] === topTwo[0][1]));
    return { category: tieWinner || winner, scores };
  }

  return { category: winner, scores };
};

export const resolveFinalImportCategory = (candidateRow = {}) => {
  const categoryHint = normalizeCategory(candidateRow?.categoryHint || candidateRow?.category);
  const comparable = resolveCandidateText(candidateRow);
  const sourceMode = String(candidateRow?.sourceMode || candidateRow?.metadata?.sourceMode || '').trim().toLowerCase();
  const isAISegmentationRow = sourceMode === 'ai_segmentation'
    || Boolean(candidateRow?.metadata?.timelineSegmentationFallback)
    || Boolean(candidateRow?.metadata?.aiOriginalCategoryHint);

  if (!comparable) {
    return categoryHint;
  }

  if (isAISegmentationRow) {
    const hasAppointmentSignal = /\bappointment\b/i.test(comparable);
    const hasClinicianSignal = /\b(?:neurolog(?:y|ist)|psychiatr(?:y|ist)|doctor|provider|specialist)\b/i.test(comparable);
    const hasMedicationChangeSignal = /\b(?:increased|decreased|stopped|started|restarted|held|missed)\b/i.test(comparable);
    const hasMedicationObjectSignal = CARE_MEDICATION_OBJECT_SIGNAL_PATTERN.test(comparable);
    const hasDirectSleepEventSignal = /\b(?:wakes?\s+up|woke\b|fell asleep|sleep duration|bedtime|overnight|slept)\b/i.test(comparable);
    const hasSleepSupportContext = /\bsleep through\b/i.test(comparable);
    const hasSleepWakeSignal = hasDirectSleepEventSignal || hasSleepSupportContext;
    const hasSideEffectSignal = /\b(?:sleepy|groggy|sluggish|side effect|side effects|fatigued|dizzy|nausea|nauseous)\b/i.test(comparable);

    if (hasAppointmentSignal && hasClinicianSignal) {
      return 'health';
    }

    if (hasSideEffectSignal && !hasMedicationChangeSignal) {
      return 'health';
    }

    if ((categoryHint === 'medication' || categoryHint === 'health') && hasSleepWakeSignal && !hasMedicationChangeSignal) {
      return 'sleep';
    }

    if (hasDirectSleepEventSignal && !hasMedicationChangeSignal) {
      return 'sleep';
    }

    if (categoryHint === 'medication') {
      if (hasMedicationChangeSignal) {
        return 'medication';
      }
      if (hasMedicationObjectSignal && !hasAppointmentSignal && !hasSideEffectSignal) {
        return 'medication';
      }
      if (hasDirectSleepEventSignal) {
        return 'sleep';
      }
      return hasAppointmentSignal || hasSideEffectSignal ? 'health' : 'daily';
    }

    if (categoryHint === 'health' && hasSleepWakeSignal && !hasAppointmentSignal) {
      return 'sleep';
    }

    return categoryHint;
  }

  if (
    categoryHint === 'sleep'
    && /\b(?:rough night|falling asleep|fell asleep|asleep|sleep|bedtime|woke up|woke)\b/i.test(comparable)
  ) {
    return 'sleep';
  }

  if (
    /\bbedtime\b/i.test(comparable)
    && /\b(?:happy|affectionate|tired|anxious|worried|excited|calm|calmer|frustrated|upset|sad|content|nervous|proud|mixed|overstimulated|more relaxed)\b/i.test(comparable)
    && !/\b(?:fell asleep|asleep|woke up|woke)\b/i.test(comparable)
  ) {
    return 'mood';
  }

  const dominant = scoreLongNoteDominantCategory(comparable);
  if (dominant.category) {
    return dominant.category;
  }

  const analysis = analyzeCategory(comparable, { allowUnknown: true });
  if (analysis?.category && analysis.category !== 'unknown') {
    return analysis.category;
  }

  return categoryHint;
};
